import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  MessageSquare,
  ArrowRight,
  Phone,
  AlertCircle
} from 'lucide-react';

const ChatbotPage = () => {
  useEffect(() => {
    // Load JotForm AI Agent script
    const script = document.createElement('script');
    script.src = 'https://cdn.jotfor.ms/s/umd/latest/for-embedded-agent.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      // Initialize JotForm Agent after script loads
      if (window.AgentInitializer) {
        window.AgentInitializer.init({
          agentRenderURL: "https://agent.jotform.com/019b8aeef7427e9fa459fcad4df8c17f5117",
          rootId: "JotFormAgent",
          formID: "019b8aeef7427e9fa459fcad4df8c17f5117",
          queryParams: ["skipWelcome=1", "maximizable=1"],
          domain: "https://www.jotform.com",
          isDraggable: false,
          background: "linear-gradient(180deg, #1E3A8A 0%, #3B82F6 100%)",
          buttonBackgroundColor: "#1E3A8A",
          buttonIconColor: "#FFFFFF",
          variant: "full-screen",
          customizations: {
            greeting: "Yes",
            greetingMessage: "Hi! I'm the Integrated Wellness AI Assistant. How can I help you today?",
            pulse: "Yes",
            position: "right"
          }
        });
      }
    };

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://cdn.jotfor.ms/s/umd/latest/for-embedded-agent.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white pt-20" data-testid="chatbot-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl font-black flex items-center gap-3 mb-3" style={{ fontFamily: 'Merriweather, serif' }}>
            <MessageSquare className="w-8 h-8" />
            AI Health Assistant
          </h1>
          <p className="text-white/90 text-lg font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
            Chat with our AI to assess symptoms and get care recommendations
          </p>
        </div>
      </div>

      {/* Emergency Warning */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="bg-red-50 border-2 border-red-500 rounded-xl p-4 flex items-center gap-4">
          <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-red-800 font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
              For medical emergencies, call 911 immediately
            </p>
            <p className="text-red-600 text-sm font-bold">
              This AI assistant is not a substitute for professional medical advice.
            </p>
          </div>
          <a href="tel:911">
            <Button 
              className="bg-red-600 hover:bg-red-700 text-white font-bold"
              style={{ fontFamily: 'Merriweather, serif' }}
            >
              <Phone className="w-4 h-4 mr-2" />
              Call 911
            </Button>
          </a>
        </div>
      </div>

      {/* JotForm AI Agent Container */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div 
          id="JotFormAgent" 
          className="w-full rounded-2xl overflow-hidden shadow-2xl border-2 border-[#1E3A8A]"
          style={{ height: '600px', minHeight: '500px' }}
        />
      </div>

      {/* Bottom CTA */}
      <div className="max-w-4xl mx-auto px-4 pb-8">
        <div className="bg-white/95 backdrop-blur-sm p-6 rounded-2xl shadow-xl border-2 border-[#1E3A8A] flex items-center justify-between">
          <div>
            <p className="font-black text-[#1E3A8A] text-lg" style={{ fontFamily: 'Merriweather, serif' }}>Ready to get started?</p>
            <p className="text-slate-600 font-bold" style={{ fontFamily: 'Merriweather, serif' }}>Download Spruce Health to book your appointment</p>
          </div>
          <div className="flex gap-4">
            <a 
              href="https://spruce.care/integratedwellness" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <button 
                className="text-white font-bold px-6 py-3 rounded-lg transition-all shadow-lg"
                style={{ 
                  background: 'linear-gradient(to right, #1E3A8A, #3B82F6)',
                  fontFamily: 'Merriweather, serif'
                }}
              >
                Download Spruce Health
              </button>
            </a>
            <Link to="/book-appointment">
              <button 
                className="text-white font-bold px-6 py-3 rounded-lg transition-all shadow-lg"
                style={{ 
                  background: 'linear-gradient(to right, #DC2626, #B91C1C)',
                  fontFamily: 'Merriweather, serif'
                }}
              >
                Book Appointment
                <ArrowRight className="w-4 h-4 inline ml-2" />
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotPage;
